/* Grainova v2 hotfix scripts (Claude). Loaded with defer from layout/theme.liquid.
   Hotfix 2 (25 Sep 2026): the trust strip under the hero ("4.9 · 18 reviews", "Dispatch in 1-2 days", ...) scrolls on
   its own when it does not fit the screen. The items are duplicated once (hidden from screen readers) so the loop is
   seamless. Pauses on hover and while touched; stays still for people who prefer reduced motion. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  const setup = (strip) => {
    if (strip.dataset.gv2Marquee) return;
    strip.dataset.gv2Marquee = '1';
    const items = [...strip.children];
    if (!items.length) return;
    const track = document.createElement('div');
    track.className = 'gv2-marquee__track';
    track.setAttribute('role', 'presentation');
    items.forEach((el) => track.appendChild(el));
    strip.appendChild(track);

    const check = () => {
      strip.classList.remove('is-marquee');
      track.querySelectorAll('[data-gv2-clone]').forEach((el) => el.remove());
      if (reduce.matches) return;
      const cs = getComputedStyle(strip);
      const room = strip.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      if (track.scrollWidth <= room + 1) return;
      items.forEach((el) => {
        const copy = el.cloneNode(true);
        copy.setAttribute('data-gv2-clone', '');
        copy.setAttribute('aria-hidden', 'true');
        copy.removeAttribute('role');
        track.appendChild(copy);
      });
      strip.classList.add('is-marquee');
      /* about 30px per second, whatever the length */
      strip.style.setProperty('--gv2-marquee-dur', Math.max(10, Math.round(track.scrollWidth / 2 / 30)) + 's');
    };

    strip.addEventListener('touchstart', () => strip.classList.add('is-paused'), { passive: true });
    strip.addEventListener('touchend', () => strip.classList.remove('is-paused'), { passive: true });
    strip.addEventListener('touchcancel', () => strip.classList.remove('is-paused'), { passive: true });
    /* round 5: keyboard users can pause it too */
    strip.addEventListener('focusin', () => strip.classList.add('is-paused'));
    strip.addEventListener('focusout', () => strip.classList.remove('is-paused'));

    let lastWidth = window.innerWidth;
    let timer;
    window.addEventListener('resize', () => {
      if (window.innerWidth === lastWidth) return; /* ignore mobile address-bar resizes */
      lastWidth = window.innerWidth;
      clearTimeout(timer);
      timer = setTimeout(check, 150);
    });
    if (reduce.addEventListener) reduce.addEventListener('change', check);
    /* Round 18 (speed): measured once, in a frame, after the fonts are ready (it used to run twice during start-up,
       each time forcing a full layout) */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => requestAnimationFrame(check)); else requestAnimationFrame(check);
  };

  const init = (root) => (root || document).querySelectorAll('.gv2-hero__trust').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  /* theme editor: re-run when the hero section is re-rendered */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();

/* v3 (25 Sep 2026): filter drawer on phones.
   "Show N products" counts live as filters are ticked (Section Rendering API, no page reload), then applies them all
   in one go. Desktop sidebar filters keep the theme's instant update. */
(() => {
  const params = (form) => {
    const p = new URLSearchParams();
    for (const [k, v] of new FormData(form)) { if (v !== '' && k !== 'page') p.append(k, v); }
    return p;
  };
  const formOf = (btn) => {
    const drawer = btn.closest('facets-drawer, .facets-drawer') || document.getElementById('facets-drawer');
    return drawer ? drawer.querySelector('form.facets, form[is="facets-form"]') : null;
  };
  let timer; let seq = 0;
  const refreshCount = (form, btn) => {
    clearTimeout(timer);
    timer = setTimeout(async () => {
      const id = ++seq;
      const p = params(form);
      p.set('section_id', btn.dataset.section);
      btn.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(`${form.getAttribute('action') || location.pathname}?${p}`, { headers: { Accept: 'text/html' } });
        const html = await res.text();
        if (id !== seq) return;
        const m = html.match(/data-gv2-count="(\d+)"/);
        if (m) {
          const n = parseInt(m[1], 10);
          btn.querySelector('[data-gv2-live-count]').textContent = n;
          const noun = btn.querySelector('[data-gv2-live-noun]'); if (noun) noun.textContent = n === 1 ? 'product' : 'products';
          btn.classList.toggle('is-zero', n === 0);
          btn.disabled = n === 0;
        }
      } catch (e) { /* keep the last count */ } finally { if (id === seq) btn.removeAttribute('aria-busy'); }
    }, 250);
  };
  document.addEventListener('change', (e) => {
    const form = e.target.closest('facets-drawer form, .facets-drawer form');
    if (!form) return;
    const btn = form.closest('facets-drawer, .facets-drawer').querySelector('[data-gv2-apply]');
    if (btn) refreshCount(form, btn);
  });
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-gv2-apply]');
    if (!btn || btn.disabled) return;
    const form = formOf(btn);
    if (!form) return;
    const p = params(form).toString();
    window.location.href = `${form.getAttribute('action') || location.pathname}${p ? `?${p}` : ''}`;
  });
})();

/* v3: cart page keeps its totals, savings and free-shipping bar in sync after quantity changes.
   Round 5: changes made while a refresh is running are queued (not dropped), failed responses fall back to a reload,
   and keyboard focus returns to the same control after the re-render. */
(() => {
  const root = () => document.querySelector('[data-gv2-cartpage]');
  if (!root()) return;
  let busy = false; let again = false;
  const focusKey = () => {
    const a = document.activeElement; const el = root();
    if (!a || !el || !el.contains(a)) return null;
    const line = a.closest('[data-line], line-item, .line-item'); const idx = line ? [...el.querySelectorAll('[data-line], line-item, .line-item')].indexOf(line) : -1;
    return { idx, name: a.getAttribute('name'), label: a.getAttribute('aria-label'), tag: a.tagName };
  };
  const restore = (k) => {
    if (!k) return; const el = root(); if (!el) return;
    const lines = [...el.querySelectorAll('[data-line], line-item, .line-item')];
    const scope = k.idx >= 0 && lines[k.idx] ? lines[k.idx] : el;
    let t = null;
    if (k.label) t = scope.querySelector(`${k.tag}[aria-label="${CSS.escape(k.label)}"]`);
    if (!t && k.name) t = scope.querySelector(`${k.tag}[name="${CSS.escape(k.name)}"]`);
    (t || el.querySelector('.gv2-cp__checkout') || el).focus({ preventScroll: true });
  };
  const refresh = async () => {
    const el = root(); if (!el) return;
    if (busy) { again = true; return; }
    busy = true;
    const k = focusKey();
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart?section_id=${el.dataset.section}`);
      if (!res.ok) throw new Error(res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const next = doc.querySelector('[data-gv2-cartpage]');
      if (next) { el.replaceWith(next); restore(k); } else window.location.reload();
    } catch (e) { window.location.reload(); } finally {
      busy = false;
      if (again) { again = false; refresh(); }
    }
  };
  document.addEventListener('cart:change', refresh);
})();

/* Round 5: "Remove" links in the cart drawer and cart page remove the line with the Cart AJAX API and refresh the
   drawer (theme's cart:refresh event), instead of reloading the whole page. If the theme already handled the click,
   this does nothing; if the request fails, the link's normal behaviour (a full page load) is used.
   Round 18 (speed): the line fades out at once, and the drawer's HTML comes back in the same request (bundled
   sections), so there is one round trip instead of two and no second fetch of the drawer. */
(() => {
  document.addEventListener('click', async (e) => {
    const a = e.target.closest('a[data-gv2-remove]');
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey) return;
    const url = new URL(a.href, location.origin);
    const line = parseInt(url.searchParams.get('line'), 10);
    const id = url.searchParams.get('id');
    if (!line && !id) return;
    e.preventDefault();
    a.setAttribute('aria-busy', 'true');
    const row = a.closest('line-item, .line-item');
    if (row) row.classList.add('gv2-li-leaving');
    try {
      const body = line ? { line, quantity: 0 } : { id, quantity: 0 };
      const S = window.GV2Store;
      const cart = S ? await S.post('cart/change.js', body) : await (await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })).json();
      if (!cart || typeof cart.item_count !== 'number') throw new Error('cart');
      if (S) S.announce(cart, 'line-item:change');
      else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (err) { window.location.href = a.href; }
  });
})();

/* v3: recipe pages open the Ingredients and Method panels, since people cook along with them. */
(() => {
  document.querySelectorAll('.shopify-section--image-with-text details.accordion').forEach((d) => {
    const t = (d.querySelector('summary') || {}).textContent || '';
    if (/ingredients|method|steps|instructions/i.test(t)) { d.open = true; }
  });
})();

/* Round 12: keep every cart count in sync without a page reload. Several add-to-cart paths (Build your box, landing
   bundles, sticky bar) only fire the theme's cart:refresh event, which re-renders the drawer but not the header badge.
   On cart:refresh we read /cart.js once and update the header badge, the "Cart (n)" text and the bottom-bar count.
   Round 18 (speed): the read goes through the shared cart store (window.GV2Store) so one /cart.js request serves the
   badge, the product cards, the cart bar and the coupon buttons, instead of each fetching its own copy. */
(() => {
  const paint = (n) => {
    document.querySelectorAll('cart-count').forEach((el) => { el.textContent = n; });
    document.querySelectorAll('cart-dot').forEach((el) => { el.classList.toggle('is-visible', n > 0); });
    document.querySelectorAll('.header__cart-dot').forEach((el) => { el.textContent = n; });
    /* only the bottom bar's badge: collection and search pages also carry a hidden [data-gv2-count] product total */
    document.querySelectorAll('.gv2-bar__count[data-gv2-count]').forEach((el) => { el.textContent = n; el.hidden = n === 0; });
  };
  window.gv2PaintCounts = paint;
  let timer = null;
  const sync = () => {
    clearTimeout(timer);
    timer = setTimeout(async () => {
      if (window.GV2Store) { window.GV2Store.get(true).catch(() => {}); return; } /* gv2:cart below paints the counts */
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!res.ok) return;
        const cart = await res.json();
        if (cart && typeof cart.item_count === 'number') paint(cart.item_count);
      } catch (e) { /* offline: leave the counts as they are */ }
    }, 60);
  };
  document.addEventListener('cart:refresh', sync);
  document.addEventListener('cart:change', (e) => { const c = e.detail && e.detail.cart; if (c && typeof c.item_count === 'number') paint(c.item_count); });
  document.addEventListener('gv2:cart', (e) => { const c = e.detail && e.detail.cart; if (c && typeof c.item_count === 'number') paint(c.item_count); });
  window.addEventListener('pageshow', (e) => { if (e.persisted) sync(); });
})();

/* Round 14: cart drawer "Order summary" toggle and the offer Copy button.
   The drawer section re-renders on every cart change, so <gv-sum> re-applies the last choice when it reconnects.
   Default: closed on every screen size (14b). Clicks are delegated, so they work on any render. */
(function () {
  if (window.gvR14) return; /* guard against the file loading twice */
  window.gvR14 = true;
  function apply(el, open) {
    el.setAttribute('data-open', open ? 'true' : 'false');
    var btn = el.querySelector('.gv-sum__toggle');
    if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  function initial() { return window.gvSumOpen === true; }
  if (window.customElements && !customElements.get('gv-sum')) {
    customElements.define('gv-sum', class extends HTMLElement {
      connectedCallback() {
        var el = this;
        if (el.querySelector('.gv-sum__toggle')) apply(el, initial());
        else setTimeout(function () { apply(el, initial()); }, 0); /* children not parsed yet */
      }
    });
  }
  function copyText(code) {
    return new Promise(function (resolve, reject) {
      var fallback = function () {
        try {
          var t = document.createElement('textarea'); t.value = code; t.setAttribute('readonly', '');
          t.style.position = 'fixed'; t.style.opacity = '0'; document.body.appendChild(t); t.select();
          var ok = document.execCommand('copy'); t.remove(); ok ? resolve() : reject();
        } catch (err) { reject(err); }
      };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(resolve, fallback);
      else fallback();
    });
  }
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var t = e.target.closest('.gv-sum__toggle');
    if (t) {
      var el = t.closest('gv-sum');
      if (el) { window.gvSumOpen = el.getAttribute('data-open') !== 'true'; apply(el, window.gvSumOpen); }
      return;
    }
    var b = e.target.closest('[data-gv-copy]');
    if (!b) return;
    copyText(b.getAttribute('data-gv-copy')).then(function () {
      b.setAttribute('data-copied', '');
      b.textContent = 'Copied';
      setTimeout(function () { b.removeAttribute('data-copied'); b.textContent = 'Copy'; }, 2000);
    }, function () {});
  });
})();

/* Round 15: one-row product carousels ([data-gv2-row] in gv2-products). The arrows scroll one screenful of cards,
   grey out at either end and hide when everything already fits. Phones keep native swiping.
   Round 17: every sideways row (.gv2-scroller: products, reviews, recipes) also gets
   - dots under the row, one per screenful, that follow the scroll and can be clicked;
   - a soft fade on the side that has more (data-gv2-more="start|end|both" on the list);
   - arrows on the row's edges, centred on the photos (their height comes from --gv2-arrow-top). */
(function () {
  if (window.gvR15) return; /* guard against the file loading twice */
  window.gvR15 = true;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var SEL = '[data-gv2-rowlist], .gv2-scroller';
  function metrics(list) {
    var first = list.firstElementChild;
    var gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    var step = first ? first.getBoundingClientRect().width + gap : list.clientWidth;
    var perView = Math.max(1, Math.floor((list.clientWidth + gap + 1) / (step || 1)));
    var page = Math.max(1, step * perView);
    var max = Math.max(0, list.scrollWidth - list.clientWidth);
    var pages = max < 4 ? 1 : Math.ceil(max / page - 0.05) + 1;
    return { page: page, max: max, pages: pages };
  }
  function dotsFor(list) {
    var d = list.__gv2dots;
    if (d && d.isConnected) return d;
    d = document.createElement('div');
    d.className = 'gv2-rowdots'; d.setAttribute('data-gv2-rowdots', '');
    var host = list.parentElement && list.parentElement.classList.contains('gv2-rowwrap') ? list.parentElement : list;
    host.insertAdjacentElement('afterend', d);
    list.__gv2dots = d;
    d.addEventListener('click', function (e) {
      var b = e.target.closest('button'); if (!b) return;
      var m = metrics(list);
      list.scrollTo({ left: Math.min(m.max, parseInt(b.getAttribute('data-i'), 10) * m.page), behavior: reduce.matches ? 'auto' : 'smooth' });
    });
    return d;
  }
  /* Round 18 (speed): all rows are measured first and changed afterwards, once per frame (reading sizes right after a
     change forces the browser to lay the whole page out again; with 4 rows that happened a dozen times at start-up).
     The first measurement comes from the ResizeObserver, which runs after the browser's own layout. */
  var queue = new Set(); var framed = false;
  function schedule(list) { queue.add(list); if (!framed) { framed = true; requestAnimationFrame(flush); } }
  function flush() {
    framed = false;
    var jobs = [];
    queue.forEach(function (list) {
      if (!list.isConnected) return;
      var m = metrics(list); var x = Math.abs(list.scrollLeft);
      var sec = list.closest('[data-gv2-row]'); var nav = sec ? sec.querySelector('[data-gv2-rownav]') : null;
      var top = null;
      if (nav) { var fig = list.querySelector('.gv2-card__figure, .product-card__figure'); var wrap = nav.parentElement; if (fig && wrap) { var fr = fig.getBoundingClientRect(); top = Math.round(fr.top - wrap.getBoundingClientRect().top + fr.height / 2); } }
      jobs.push({ list: list, m: m, x: x, sec: sec, nav: nav, top: top });
    });
    queue.clear();
    jobs.forEach(write);
  }
  function write(j) {
    var list = j.list, m = j.m, x = j.x;
    var atStart = x <= 2, atEnd = x >= m.max - 2;
    var more = m.max < 4 ? 'none' : atStart ? 'end' : atEnd ? 'start' : 'both';
    if (list.getAttribute('data-gv2-more') !== more) list.setAttribute('data-gv2-more', more);
    var d = dotsFor(list);
    if (d.hidden !== (m.pages < 2)) d.hidden = m.pages < 2;
    if (d.children.length !== m.pages) {
      var h2 = list.closest('section') && list.closest('section').querySelector('h2');
      var label = list.getAttribute('aria-label') || (h2 ? h2.textContent.trim() : 'items');
      var h = '';
      for (var i = 0; i < m.pages; i++) h += '<button type="button" data-i="' + i + '" aria-label="' + label.replace(/"/g, '') + ': screen ' + (i + 1) + ' of ' + m.pages + '"></button>';
      d.innerHTML = h;
    }
    var cur = atEnd ? m.pages - 1 : Math.min(m.pages - 1, Math.round(x / m.page));
    [].forEach.call(d.children, function (b, i) { var on = i === cur; if (on !== (b.getAttribute('aria-current') === 'true')) { if (on) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current'); } });
    if (j.nav) {
      if (j.nav.hidden !== (m.max < 4)) j.nav.hidden = m.max < 4;
      var btns = j.nav.querySelectorAll('[data-gv2-dir]');
      if (btns.length === 2) { btns[0].disabled = atStart; btns[1].disabled = atEnd; }
      if (j.top !== null) j.sec.style.setProperty('--gv2-arrow-top', j.top + 'px');
    }
  }
  function update(list) { schedule(list); }
  function init(root) {
    (root || document).querySelectorAll(SEL).forEach(function (list) {
      if (list.getAttribute('data-gv2-row-ready')) { update(list); return; }
      list.setAttribute('data-gv2-row-ready', '1');
      list.addEventListener('scroll', function () { update(list); }, { passive: true });
      if (window.ResizeObserver) new ResizeObserver(function () { update(list); }).observe(list); /* also gives the first measurement */
      else { window.addEventListener('resize', function () { update(list); }); update(list); }
    });
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-gv2-dir]') : null;
    if (!btn) return;
    var sec = btn.closest('[data-gv2-row]');
    var list = sec ? sec.querySelector('[data-gv2-rowlist]') : null;
    if (!list) return;
    var m = metrics(list);
    list.scrollBy({ left: parseInt(btn.getAttribute('data-gv2-dir'), 10) * m.page, behavior: reduce.matches ? 'auto' : 'smooth' });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();

/* Round 15: the box page opened from a product page's "Box of 3 · save 10%" card (?pick=<handle>) starts with that
   product already in the box. Only on pages with the box builder in bundle mode; unknown handles are ignored. */
(function () {
  var pickOnce = function () {
    var want;
    try { want = new URLSearchParams(window.location.search).get('pick'); } catch (e) { return; }
    if (!want) return;
    var box = document.querySelector('gv2-box[data-bundle]');
    if (!box || (box.picked && box.picked.length)) return;
    var btn = null;
    box.querySelectorAll('[data-pick]').forEach(function (b) { if (!btn && b.getAttribute('data-id') === want) btn = b; });
    if (btn && !btn.disabled) btn.click();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', pickOnce);
  else pickOnce();
})();

/* Round 17: coupons, Zomato / Swiggy / Blinkit style (replaces the Round 16 Apply-only code).
   - Every [data-gv2-apply-code] button (cart row, product page card, home offer, pop-up) applies its code with the Cart
     AJAX API and then turns into "Remove"; tapping it again takes the code off. Buttons read /cart.js on load, so a
     code applied earlier already shows "Remove".
   - The cart row's "View all coupons" opens a "Coupons & offers" sheet: a scrollable list of every coupon (from the
     grainova.coupons shop metafield, rendered by snippets/gv2-coupons.liquid) with Apply / Remove, the saving on this
     cart, the reason a coupon is locked ("Add Rs. 120 more", "Not valid on Build your box"), a box to type any code,
     and the automatic offers (free shipping, Build your box).
   - One coupon at a time by default (like Zomato and Swiggy): applying another replaces it.
   - Changes are sent with the drawer's and cart page's sections bundled in, then announced with the theme's cart:change
     event, so the drawer, cart page and header counts update without another request. While the sheet is open inside
     the drawer, the announcement waits until the sheet closes (a re-render would close it).
   - Removing a code also clears Shopify's discount_code cookie so the code can't come back at checkout. */
(function () {
  if (window.gvR16) return; /* guard against the file loading twice */
  window.gvR16 = true;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var json = function (r) { if (!r.ok) return r.json().then(function (b) { var e = new Error(b && (b.description || b.message) || r.status); e.status = r.status; throw e; }, function () { throw new Error(r.status); }); return r.json(); };
  var up = function (c) { return String(c == null ? '' : c).trim().toUpperCase(); };
  var esc = function (s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (ch) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]; }); };

  /* ---- shared cart store, window.GV2Store (also used by the Round 17 card steppers and cart bar; the product page's sticky bar keeps its own window.GV2Cart) ---- */
  var G = window.GV2Store = window.GV2Store || {};
  var inflight = null; var held = null;
  var rt = function () { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || root; };
  G.cart = null;
  G.set = function (c) { if (!c || typeof c.item_count !== 'number') return; G.cart = c; document.dispatchEvent(new CustomEvent('gv2:cart', { detail: { cart: c } })); };
  G.get = function (force) {
    if (G.cart && !force) return Promise.resolve(G.cart);
    if (inflight) return inflight;
    inflight = fetch(rt() + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(json).then(function (c) { inflight = null; G.set(c); return c; }, function (e) { inflight = null; throw e; });
    return inflight;
  };
  G.sections = function () {
    var list = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: list } }));
    return list.filter(function (x, i) { return x && list.indexOf(x) === i; });
  };
  G.post = function (path, body) {
    var s = G.sections(); if (s.length) body.sections = s.join(',');
    return fetch(rt() + path, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }).then(json);
  };
  G.hold = function (on) { held = on ? (held || { cart: null }) : null; };
  G.announce = function (cart, base) {
    G.set(cart);
    if (held) { held.cart = cart; held.base = base; return; }
    if (cart && cart.sections) document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: base || 'gv2:update', cart: cart } }));
    else document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  };
  G.release = function () { var h = held; held = null; if (h && h.cart) G.announce(h.cart, h.base); };
  G.money = function (cents) {
    var d = data(); var f = (d && d.fmt) || 'Rs. {{amount}}';
    var v = Math.round(cents) / 100; var n = v % 1 ? v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v.toLocaleString('en-IN');
    return f.replace(/\{\{\s*\w+\s*\}\}/, n);
  };
  document.addEventListener('cart:change', function (e) { var c = e.detail && e.detail.cart; if (c && c !== G.cart) G.set(c); });
  /* (cart:refresh re-reads /cart.js through the Round 12 block above) */

  /* Round 18 (speed): quantity changes from product cards don't wait for the drawer's HTML. The cart JSON comes back
     fast; the drawer is re-rendered once, in the background, 0.6 s after the last change (or at once if it opens). */
  var drawerTimer = null;
  G.busy = function () { return false; }; /* replaced by the card block while a change is on its way */
  G.flushDrawer = function () {
    clearTimeout(drawerTimer); drawerTimer = null;
    var ids = G.sections(); if (!ids.length || !G.cart) return Promise.resolve();
    var at = G.cart;
    return fetch(rt() + '?sections=' + encodeURIComponent(ids.join(',')), { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(json).then(function (secs) {
      if (G.busy() || G.cart !== at || held) { G.syncDrawer(); return; } /* something changed meanwhile: try again */
      var c = {}; for (var k in at) c[k] = at[k]; c.sections = secs;
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'gv2:sync', cart: c } }));
    }).catch(function () { document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); });
  };
  G.syncDrawer = function (ms) { clearTimeout(drawerTimer); drawerTimer = setTimeout(function () { if (G.busy()) G.syncDrawer(ms); else G.flushDrawer(); }, ms || 600); };
  document.addEventListener('dialog:before-show', function (e) { if (drawerTimer && e.target && e.target.matches && e.target.matches('cart-drawer')) G.flushDrawer(); }, true);
  G.counts = function (n) { if (window.gv2PaintCounts) window.gv2PaintCounts(n); };
  /* read the cart once the page is idle, so the first tap never waits for /cart.js */
  var warm = function () { if (!G.cart) G.get().catch(function () {}); };
  if ('requestIdleCallback' in window) window.addEventListener('load', function () { requestIdleCallback(warm, { timeout: 2500 }); });
  else window.addEventListener('load', function () { setTimeout(warm, 1200); });
  window.addEventListener('pageshow', function (e) { if (e.persisted) G.get(true).catch(function () {}); });

  /* ---- coupon data and maths ---- */
  function data() {
    var el = document.querySelector('[data-gv2-cartpage] [data-gv2-coupons]') || document.querySelector('[data-gv2-coupons]');
    if (!el) return null;
    if (el.__gv2 === undefined) { try { el.__gv2 = JSON.parse(el.textContent); } catch (e) { el.__gv2 = null; } }
    return el.__gv2;
  }
  function list() { var d = data(); return ((d && d.coupons) || []).filter(function (c) { return c && c.code && !c.hidden; }); }
  function find(code) { var u = up(code); return list().filter(function (c) { return up(c.code) === u; })[0] || null; }
  function codesOf(cart) { return ((cart && cart.discount_codes) || []).filter(function (d) { return d && d.code; }); }
  function hasCode(cart, code) { var u = up(code); return codesOf(cart).filter(function (d) { return up(d.code) === u; })[0] || null; }
  function savedBy(cart, code) {
    var u = up(code); var t = 0;
    ((cart && cart.items) || []).forEach(function (it) { (it.line_level_discount_allocations || []).forEach(function (a) { if (a.discount_application && up(a.discount_application.title) === u) t += a.amount; }); });
    ((cart && cart.cart_level_discount_applications) || []).forEach(function (a) { if (up(a.title) === u) t += a.total_allocated_amount; });
    return t;
  }
  function excludedNote(c) { return c.exclude_note || (c.exclude_tag === 'gv-box' ? 'Not valid on Build your box' : 'Not valid on the items in your cart'); }
  function assess(c, cart) {
    var d = data() || {}; var tags = d.tags || {}; var elig = 0;
    ((cart && cart.items) || []).forEach(function (it) { var tg = tags[it.key] || []; if (c.exclude_tag && tg.indexOf(c.exclude_tag) !== -1) return; elig += it.original_line_price; });
    var sub = (cart && (cart.original_total_price || cart.items_subtotal_price)) || 0; var min = (c.min || 0) * 100;
    var save = c.percent ? Math.floor(elig * c.percent / 10000) * 100 : Math.min((c.amount || 0) * 100, elig); /* whole rupees */
    var on = hasCode(cart, c.code);
    if (on) return { state: 'on', save: savedBy(cart, c.code) || save, note: on.applicable ? '' : 'Saved · applies to eligible items' };
    if (!cart || !cart.item_count) return { state: 'ok', save: 0, note: 'Applies once you add products' };
    if (elig <= 0) return { state: 'locked', save: 0, note: excludedNote(c) };
    if (sub < min) return { state: 'locked', save: save, note: 'Add ' + G.money(min - sub) + ' more to unlock' };
    return { state: 'ok', save: save, note: save > 0 ? 'Save ' + G.money(save) + ' on this order' : '' };
  }

  /* ---- apply / remove ---- */
  function setCodes(codes) { return G.post('cart/update.js', { discount: codes.join(',') }); }
  function clearCookie() { try { document.cookie = 'discount_code=; Max-Age=0; path=/'; } catch (e) { /* ignore */ } }
  /* Round 18 (speed): no /cart.js read before the change when the cart is already known (one round trip, not two).
     With one coupon at a time the new list is simply [code]. */
  var known = function () { var d = data() || {}; return (G.cart || d.one !== false) ? Promise.resolve(G.cart || { discount_codes: [] }) : G.get(true); };
  G.apply = function (code) {
    return known().then(function (cart) {
      var d = data() || {}; var one = d.one !== false; var u = up(code);
      var keep = codesOf(cart).map(function (x) { return x.code; }).filter(function (x) { return up(x) !== u && !one; });
      var before = codesOf(cart).map(function (x) { return x.code; });
      return setCodes(keep.concat([code])).then(function (next) {
        var hit = hasCode(next, code);
        return { cart: next, applicable: !!(hit && hit.applicable), found: !!hit, before: before, replaced: before.filter(function (x) { return up(x) !== u; }) };
      });
    });
  };
  G.remove = function (code) {
    return known().then(function (cart) {
      var u = up(code);
      clearCookie();
      return setCodes(codesOf(cart).map(function (x) { return x.code; }).filter(function (x) { return up(x) !== u; }));
    });
  };
  window.GV2ApplyCode = G.apply; /* Round 16 name, kept for other callers */

  /* ---- toast ("WELCOME10 applied · You saved Rs. 43") ---- */
  var toastTimer;
  function toast(title, sub, kind) {
    var t = document.querySelector('.gv2-ctoast');
    if (!t) { t = document.createElement('div'); t.className = 'gv2-ctoast'; t.setAttribute('role', 'status'); t.setAttribute('aria-live', 'polite'); document.body.appendChild(t); }
    t.setAttribute('data-kind', kind || 'ok');
    t.innerHTML = '<span class="gv2-ctoast__icon" aria-hidden="true">' + (kind === 'off' ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>' : '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>') + '</span><span class="gv2-ctoast__text"><strong>' + esc(title) + '</strong>' + (sub ? '<span>' + esc(sub) + '</span>' : '') + '</span>';
    t.removeAttribute('data-show'); void t.offsetWidth; t.setAttribute('data-show', '');
    clearTimeout(toastTimer); toastTimer = setTimeout(function () { t.removeAttribute('data-show'); }, 2600);
  }
  G.toast = toast;

  /* ---- Apply / Remove buttons outside the sheet ---- */
  function label(btn) { return btn.querySelector('[data-gv2-apply-label]') || btn; }
  function show(btn, state, text, msg) {
    var l = label(btn);
    if (!btn.hasAttribute('data-gv2-label')) btn.setAttribute('data-gv2-label', l.textContent);
    btn.setAttribute('data-state', state);
    l.textContent = text || btn.getAttribute('data-gv2-label');
    var code = btn.getAttribute('data-gv2-apply-code');
    if (state === 'on') { btn.setAttribute('aria-pressed', 'true'); btn.setAttribute('aria-label', 'Remove code ' + code); }
    else if (state === 'idle') { btn.setAttribute('aria-pressed', 'false'); btn.setAttribute('aria-label', 'Apply code ' + code); }
    var scope = btn.closest('[data-gv2-apply-scope]');
    var out = scope ? scope.querySelector('[data-gv2-apply-msg]') : null;
    if (out && msg !== undefined) { out.textContent = msg || ''; out.hidden = !msg; }
  }
  function paintButtons(cart) {
    document.querySelectorAll('[data-gv2-apply-code]').forEach(function (btn) {
      var st = btn.getAttribute('data-state');
      if (st === 'busy') return;
      var code = btn.getAttribute('data-gv2-apply-code'); var on = hasCode(cart, code);
      if (on) { if (st !== 'on') show(btn, 'on', 'Remove', code + (on.applicable ? ' is applied. The saving shows in your cart.' : ' is saved. It applies to eligible items.')); }
      else if (st === 'on') show(btn, 'idle', null, '');
    });
  }
  document.addEventListener('gv2:cart', function (e) { paintButtons(e.detail.cart); });
  function savedMsg(code, res) {
    var c = find(code);
    if (res.cart && res.cart.item_count > 0) return code + ' is saved. ' + (c && c.exclude_tag === 'gv-box' ? 'It takes ' + (c.percent ? c.percent + '% ' : '') + 'off eligible items (not Build your box).' : 'It applies to eligible items.');
    return code + ' is saved. It applies once you add products.';
  }
  /* Round 18: while a coupon change is on its way the drawer's totals are dimmed, so it's clear they're updating */
  function updating(on) { document.querySelectorAll('cart-drawer, [data-gv2-cartpage]').forEach(function (d) { if (on) d.setAttribute('data-gv2-updating', ''); else d.removeAttribute('data-gv2-updating'); }); }
  document.addEventListener('cart:change', function () { updating(false); });
  function applyBtn(btn) {
    var code = btn.getAttribute('data-gv2-apply-code');
    if (!code || btn.getAttribute('data-state') === 'busy') return;
    var inDrawer = !!btn.closest('cart-drawer');
    show(btn, 'busy', 'Applying…');
    updating(true);
    G.apply(code).then(function (res) {
      var saved = savedBy(res.cart, code);
      if (res.applicable) show(btn, 'on', 'Remove', code + ' is applied. The saving shows in your cart.');
      else show(btn, 'on', 'Remove', savedMsg(code, res));
      G.announce(res.cart, 'discount:change');
      if (res.applicable) toast(code + ' applied', saved > 0 ? 'You saved ' + G.money(saved) : (inDrawer ? '' : 'The saving shows in your cart'));
    }).catch(function () {
      updating(false);
      var done = function () { show(btn, 'idle', 'Copied', 'Couldn\'t apply automatically. ' + code + ' is copied; paste it at checkout.'); };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(done, function () { show(btn, 'idle', null, 'Use code ' + code + ' at checkout.'); });
      else show(btn, 'idle', null, 'Use code ' + code + ' at checkout.');
    });
  }
  /* Round 18 (speed): Remove answers at once. The button flips back to Apply, the drawer's coupon row shows
     "CODE removed" and the toast appears straight away; the totals follow when Shopify replies (one round trip).
     If the request fails, everything flips back and says so. */
  function rowRemoved(row, code) {
    if (!row) return;
    row.classList.remove('gv2-crow--on'); row.classList.add('gv2-crow--gone');
    var t = row.querySelector('.gv2-crow__title'); if (t) t.innerHTML = '<span class="gv2-crow__code">' + esc(code) + '</span> removed';
    var icon = row.querySelector('.gv2-crow__icon'); if (icon) icon.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12V4h8l10 10-8 8z"/><circle cx="7.5" cy="8.5" r="1.5"/></svg>';
    var b = row.querySelector('[data-gv2-coupon-remove]');
    if (b) { b.removeAttribute('data-gv2-coupon-remove'); b.setAttribute('data-gv2-apply-code', code); b.classList.remove('gv2-crow__btn--remove'); b.setAttribute('aria-label', 'Apply coupon ' + code); b.innerHTML = '<span data-gv2-apply-label>Apply</span>'; b.removeAttribute('aria-busy'); }
  }
  function removeCode(code, btn, row) {
    if (btn && btn.getAttribute('data-state') === 'busy') return;
    var was = btn ? btn.getAttribute('data-state') : null;
    if (btn) show(btn, 'busy', 'Apply');
    if (row) rowRemoved(row, code);
    updating(true);
    toast(code + ' removed', 'You can apply it again any time', 'off');
    return G.remove(code).then(function (cart) {
      if (btn) show(btn, 'idle', null, '');
      G.announce(cart, 'discount:change');
      return cart;
    }).catch(function () {
      updating(false);
      if (btn) show(btn, was || 'on', 'Remove', 'Couldn\'t remove the code. Please try again.');
      toast('Couldn\'t remove ' + code, 'Please check your connection and try again', 'off');
      G.get(true).then(function () { document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); }, function () {});
    });
  }
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var btn = e.target.closest('[data-gv2-apply-code]');
    if (btn) { e.preventDefault(); if (btn.getAttribute('data-state') === 'on') removeCode(btn.getAttribute('data-gv2-apply-code'), btn); else applyBtn(btn); return; }
    var rm = e.target.closest('[data-gv2-coupon-remove]');
    if (rm) { e.preventDefault(); removeCode(rm.getAttribute('data-gv2-coupon-remove'), null, rm.closest('[data-gv2-crow]')); return; }
    var op = e.target.closest('[data-gv2-cs-open]');
    if (op) { e.preventDefault(); openSheet(op); }
  });
  /* pop-up success state: apply straight away (unless already applied) */
  var auto = function () {
    var b = document.querySelectorAll('[data-gv2-apply-code][data-gv2-apply-auto]');
    if (!b.length) return;
    G.get().then(function (cart) { b.forEach(function (x) { if (!x.getAttribute('data-state') && !hasCode(cart, x.getAttribute('data-gv2-apply-code'))) applyBtn(x); }); }, function () { b.forEach(function (x) { if (!x.getAttribute('data-state')) applyBtn(x); }); });
  };
  var boot = function () {
    auto();
    if (document.querySelector('[data-gv2-apply-code]')) G.get().then(paintButtons, function () {});
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  /* ---- the Coupons & offers sheet ---- */
  var sheet = null; var opener = null; var dirty = false;
  function build() {
    var el = document.createElement('div');
    el.className = 'gv2-cs';
    el.setAttribute('data-gv2-cs', '');
    el.innerHTML = '<div class="gv2-cs__scrim" data-gv2-cs-close></div>' +
      '<div class="gv2-cs__panel" role="dialog" aria-modal="true" aria-labelledby="gv2-cs-title" tabindex="-1">' +
        '<div class="gv2-cs__grab" aria-hidden="true"></div>' +
        '<div class="gv2-cs__head"><p class="gv2-cs__title" id="gv2-cs-title">Coupons &amp; offers</p>' +
        '<button type="button" class="gv2-cs__x" data-gv2-cs-close aria-label="Close coupons"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>' +
        '<form class="gv2-cs__enter" data-gv2-cs-form novalidate><label class="sr-only" for="gv2-cs-code">Coupon code</label>' +
        '<input id="gv2-cs-code" name="code" type="text" placeholder="Enter coupon code" autocomplete="off" autocapitalize="characters" spellcheck="false" enterkeyhint="done">' +
        '<button type="submit">Apply</button></form>' +
        '<p class="gv2-cs__err" data-gv2-cs-err role="alert" hidden></p>' +
        '<div class="gv2-cs__body" data-gv2-cs-body></div>' +
      '</div>';
    return el;
  }
  function stub(c) {
    if (c.percent) return c.percent + '% OFF';
    if (c.amount) return G.money(c.amount * 100).replace(/\s/g, ' ') + ' OFF';
    return 'OFFER';
  }
  function card(c, a, extra) {
    var btn = a.state === 'on'
      ? '<button type="button" class="gv2-cpn__btn gv2-cpn__btn--on" data-gv2-cs-remove="' + esc(c.code) + '" aria-label="Remove ' + esc(c.code) + '">Remove</button>'
      : '<button type="button" class="gv2-cpn__btn" data-gv2-cs-apply="' + esc(c.code) + '"' + (a.state === 'locked' ? ' disabled aria-disabled="true"' : '') + ' aria-label="Apply ' + esc(c.code) + '">Apply</button>';
    return '<li class="gv2-cpn" data-state="' + a.state + '" data-code="' + esc(up(c.code)) + '">' +
      '<span class="gv2-cpn__stub" aria-hidden="true"><span>' + esc(stub(c)) + '</span></span>' +
      '<div class="gv2-cpn__main">' +
        '<p class="gv2-cpn__top"><span class="gv2-cpn__code">' + esc(c.code) + '</span>' + (c.label ? '<span class="gv2-cpn__label">' + esc(c.label) + '</span>' : '') + (a.state === 'on' ? '<span class="gv2-cpn__applied">Applied</span>' : '') + '</p>' +
        (c.title ? '<p class="gv2-cpn__title">' + esc(c.title) + '</p>' : '') +
        (a.state === 'on' && a.save > 0 ? '<p class="gv2-cpn__save">You saved ' + esc(G.money(a.save)) + '</p>' : '') +
        (a.state !== 'on' && a.note ? '<p class="gv2-cpn__note' + (a.state === 'ok' && a.save > 0 ? ' gv2-cpn__note--save' : '') + '">' + esc(a.note) + '</p>' : '') +
        (a.state === 'on' && a.note ? '<p class="gv2-cpn__note">' + esc(a.note) + '</p>' : '') +
        (c.text ? '<p class="gv2-cpn__text">' + esc(c.text) + '</p>' : '') + (extra || '') +
      '</div>' + btn + '</li>';
  }
  function render() {
    if (!sheet) return;
    var cart = G.cart || { items: [], item_count: 0 }; var d = data() || {};
    var rows = list().map(function (c) { return { c: c, a: assess(c, cart) }; });
    /* codes applied by hand that are not in the list */
    codesOf(cart).forEach(function (x) { if (!find(x.code)) rows.push({ c: { code: x.code, title: 'Coupon code' }, a: { state: 'on', save: savedBy(cart, x.code), note: x.applicable ? '' : 'Not valid for the items in your cart' } }); });
    var rank = { on: 0, ok: 1, locked: 2 };
    rows.sort(function (p, q) { return (rank[p.a.state] - rank[q.a.state]) || (q.a.save - p.a.save); });
    var html = '<p class="gv2-cs__sec">' + (rows.length ? 'Available coupons' : 'No coupons right now') + '</p>';
    if (rows.length) html += '<ul class="gv2-cs__list" role="list">' + rows.map(function (r) { return card(r.c, r.a); }).join('') + '</ul>';
    if (d.one !== false && rows.length > 1) html += '<p class="gv2-cs__hint">One coupon per order. Applying another one replaces the current coupon.</p>';
    /* automatic offers */
    var th = d.threshold || 0; var tot = cart.total_price || 0; var more = th - tot;
    html += '<p class="gv2-cs__sec">More ways to save</p><ul class="gv2-cs__list gv2-cs__list--auto" role="list">';
    if (th > 0) html += '<li class="gv2-cpn gv2-cpn--auto" data-state="' + (more > 0 ? 'locked' : 'on') + '"><span class="gv2-cpn__stub" aria-hidden="true"><span>FREE SHIP</span></span><div class="gv2-cpn__main"><p class="gv2-cpn__title">Free shipping on orders above ' + esc(G.money(th)) + '</p><p class="gv2-cpn__note' + (more > 0 ? '' : ' gv2-cpn__note--save') + '">' + (more > 0 ? 'Add ' + esc(G.money(more)) + ' more to unlock' : 'Unlocked on this order') + '</p><p class="gv2-cpn__text">Applied automatically, no code needed.</p></div></li>';
    html += '<li class="gv2-cpn gv2-cpn--auto" data-state="ok"><span class="gv2-cpn__stub" aria-hidden="true"><span>BOX 10%</span></span><div class="gv2-cpn__main"><p class="gv2-cpn__title">Build your box: any 3 packs, 10% off</p><p class="gv2-cpn__text">Mix attas, millets and breakfast mixes. Priced as a bundle, no code needed.</p></div><a class="gv2-cpn__btn gv2-cpn__btn--link" href="' + root + 'products/build-your-box">Build</a></li>';
    html += '</ul>';
    sheet.querySelector('[data-gv2-cs-body]').innerHTML = html;
  }
  function err(msg) { if (!sheet) return; var e = sheet.querySelector('[data-gv2-cs-err]'); e.textContent = msg || ''; e.hidden = !msg; }
  function busy(btn, text) { if (!btn) return; btn.setAttribute('data-state', 'busy'); btn.disabled = true; btn.textContent = text; }
  function openSheet(from) {
    closeSheet(true);
    opener = from || null; dirty = false;
    var drawer = from && from.closest ? from.closest('cart-drawer') : null;
    sheet = build();
    if (drawer) { sheet.classList.add('gv2-cs--drawer'); sheet.setAttribute('slot', 'footer'); drawer.appendChild(sheet); G.hold(true); }
    else { sheet.classList.add('gv2-cs--page'); document.body.appendChild(sheet); document.documentElement.classList.add('gv2-cs-lock'); }
    render();
    G.get(true).then(render, function () {});
    requestAnimationFrame(function () { if (sheet) { sheet.setAttribute('data-open', ''); var x = sheet.querySelector('.gv2-cs__x'); if (x) x.focus({ preventScroll: true }); } });
  }
  function closeSheet(quiet) {
    if (!sheet) return;
    var el = sheet; sheet = null;
    document.documentElement.classList.remove('gv2-cs-lock');
    el.removeAttribute('data-open');
    setTimeout(function () { el.remove(); }, quiet ? 0 : 220);
    var o = opener; opener = null;
    G.release();
    if (!quiet && o && o.isConnected) o.focus({ preventScroll: true });
    else if (!quiet) { var r = document.querySelector('cart-drawer [data-gv2-cs-open]'); if (r) r.focus({ preventScroll: true }); }
  }
  G.openCoupons = openSheet; G.closeCoupons = closeSheet;
  function unbusy(btn) { if (btn && btn.isConnected && btn.closest('[data-gv2-cs-form]')) { btn.removeAttribute('data-state'); btn.disabled = false; btn.textContent = 'Apply'; } }
  function sheetApply(code, btn) {
    err('');
    busy(btn, 'Applying…');
    return G.apply(code).then(function (res) {
      unbusy(btn);
      var known = find(code);
      if (!res.applicable && (!known || !res.cart.item_count || !res.found)) {
        /* invalid or not usable here: put the previous codes back */
        return setCodes(res.before).then(function (back) {
          G.set(back); render();
          err(known ? '“' + up(code) + '” can\'t be used on this cart. Check the offer\'s terms.' : '“' + up(code) + '” isn\'t a valid code for this cart.');
          dirty = true;
        });
      }
      var saved = savedBy(res.cart, code);
      G.announce(res.cart, 'discount:change');
      closeSheet();
      toast(up(code) + ' applied', saved > 0 ? 'You saved ' + G.money(saved) + (res.replaced.length ? ' · replaced ' + res.replaced.join(', ') : '') : (res.applicable ? '' : 'Saved · applies to eligible items'));
    }).catch(function (e2) { unbusy(btn); render(); err('Couldn\'t apply the code (' + (e2 && e2.message || 'network') + '). Please try again.'); });
  }
  document.addEventListener('click', function (e) {
    if (!sheet || !e.target.closest) return;
    if (e.target.closest('[data-gv2-cs-close]')) { e.preventDefault(); closeSheet(); return; }
    var a = e.target.closest('[data-gv2-cs-apply]');
    if (a && !a.disabled) { e.preventDefault(); sheetApply(a.getAttribute('data-gv2-cs-apply'), a); return; }
    var r = e.target.closest('[data-gv2-cs-remove]');
    if (r) {
      e.preventDefault(); err(''); var code = r.getAttribute('data-gv2-cs-remove'); busy(r, 'Removing…');
      G.remove(code).then(function (cart) { G.announce(cart, 'discount:change'); dirty = true; render(); var f = sheet && sheet.querySelector('[data-code="' + up(code) + '"] .gv2-cpn__btn'); if (f) f.focus({ preventScroll: true }); toast(up(code) + ' removed', '', 'off'); })
        .catch(function () { render(); err('Couldn\'t remove the code. Please try again.'); });
    }
  });
  document.addEventListener('submit', function (e) {
    if (!sheet || !e.target.matches || !e.target.matches('[data-gv2-cs-form]')) return;
    e.preventDefault();
    var input = e.target.querySelector('input'); var code = up(input.value);
    if (!code) { err('Type a coupon code first.'); input.focus(); return; }
    if (hasCode(G.cart, code)) { err(code + ' is already applied.'); return; }
    sheetApply(code, e.target.querySelector('button'));
  }, true);
  document.addEventListener('keydown', function (e) {
    if (!sheet) return;
    if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); closeSheet(); return; }
    if (e.key === 'Tab') { /* keep focus inside the sheet (handled here so the drawer's own focus trap doesn't fight it) */
      var f = [].slice.call(sheet.querySelectorAll('button:not([disabled]), input, a[href]')).filter(function (x) { return x.offsetParent !== null || x === document.activeElement; });
      if (!f.length) return;
      e.preventDefault(); e.stopImmediatePropagation();
      var i = f.indexOf(document.activeElement);
      var next = i === -1 ? (e.shiftKey ? f.length - 1 : 0) : (i + (e.shiftKey ? -1 : 1) + f.length) % f.length;
      f[next].focus();
    }
  }, true);
  document.addEventListener('gv2:cart', function () { if (sheet) render(); });
  /* the drawer closing takes the sheet with it */
  document.addEventListener('dialog:after-hide', function (e) { if (sheet && e.target && e.target.matches && e.target.matches('cart-drawer')) closeSheet(true); }, true);
})();

/* Round 16: "Build a box" links. On the home page "/#build-a-box" scrolls to the box section; from any other page it
   now opens the box's own page (/products/build-your-box) instead of loading the home page and jumping down.
   Covers the menu, mobile menu, mega-menu, bottom bar, category row, cart and Our story links. */
(function () {
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;
    var href = a.getAttribute('href') || '';
    if (!/^(https?:\/\/[^/]+)?\/?#build-a-box$/.test(href)) return;
    var here = document.getElementById('build-a-box');
    if (window.location.pathname === root && here) return; /* home: normal jump to the section */
    e.preventDefault();
    if (here && /\/products\/build-your-box\/?$/.test(window.location.pathname)) { here.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    window.location.href = root + 'products/build-your-box';
  });
})();

/* Round 16: compact sticky header. Adds html[data-gv2-scrolled] once the page is scrolled (hysteresis so it can't
   flicker when the header height changes); the CSS in gv2-polish.css section 13 shrinks the logo and padding. */
(function () {
  var el = document.documentElement, on = false, queued = false;
  var check = function () {
    queued = false;
    var y = window.scrollY || window.pageYOffset || 0;
    if (!on && y > 160) { on = true; el.setAttribute('data-gv2-scrolled', ''); }
    else if (on && y < 40) { on = false; el.removeAttribute('data-gv2-scrolled'); }
  };
  window.addEventListener('scroll', function () { if (!queued) { queued = true; window.requestAnimationFrame(check); } }, { passive: true });
  window.requestAnimationFrame(check); /* Round 18: first check in a frame, not during start-up */
})();

/* Round 17: quick-commerce add to cart on product cards (Blinkit / Zepto / Instamart pattern).
   "+ Add" on a single-size card adds one pack without opening the cart drawer and turns into a − n + stepper;
   a floating "n items · Rs. total · View cart" bar appears (phones: above the bottom bar; desktop: bottom centre) on
   home, collection, search and content pages. The drawer, header count and cart page update from the same response. */
(function () {
  if (window.gvR17) return; /* guard against the file loading twice */
  window.gvR17 = true;
  var G = window.GV2Store; if (!G) return;
  var esc = function (s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (ch) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]; }); };
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  function qtyOf(cart, vid) { var n = 0; ((cart && cart.items) || []).forEach(function (it) { if (String(it.variant_id) === String(vid)) n += it.quantity; }); return n; }
  function vidOf(form) { var i = form.querySelector('input[name="id"]'); return i ? i.value : null; }
  function nameOf(form) { var c = form.closest('product-card, .gv2-card'); var t = c && c.querySelector('.gv2-card__title, .product-title'); return t ? t.textContent.trim() : 'this product'; }
  function paintForm(form, n) {
    var step = form.querySelector('[data-gv2-step]');
    if (n > 0) {
      if (!step) {
        step = document.createElement('div');
        step.className = 'gv2-step'; step.setAttribute('data-gv2-step', '');
        var nm = esc(nameOf(form));
        step.innerHTML = '<button type="button" class="gv2-step__btn" data-gv2-step-dir="-1" aria-label="Remove one ' + nm + '"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14"/></svg></button>' +
          '<span class="gv2-step__n" aria-live="polite"></span>' +
          '<button type="button" class="gv2-step__btn" data-gv2-step-dir="1" aria-label="Add one more ' + nm + '"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg></button>';
        form.appendChild(step);
      }
      step.querySelector('.gv2-step__n').textContent = n;
      step.querySelector('.gv2-step__n').setAttribute('aria-label', n + ' in cart');
      form.setAttribute('data-gv2-qty', n);
    } else {
      form.removeAttribute('data-gv2-qty');
      if (step) {
        var focused = step.contains(document.activeElement);
        step.remove();
        if (focused) { var b = form.querySelector('.gv2-card__add'); if (b) b.focus({ preventScroll: true }); }
      }
    }
  }
  /* Round 18 (speed): every tap answers at once. The card shows the new number (or "+ Add" again at 0) and the cart
     bar and badge move straight away; one request per product runs in the background with the latest wanted quantity
     (taps made while it runs are merged into the next request, never ignored). Adds from "+ Add" still use
     /cart/add.js so Shopify's add-to-cart analytics keep firing; other changes use /cart/change.js without the
     drawer's HTML, which is re-rendered once in the background afterwards (GV2Store.syncDrawer). */
  var pend = {}; /* variant id -> { want, busy } */
  G.busy = function () { for (var k in pend) if (pend[k].busy) return true; return false; };
  function wantOf(vid) { var p = pend[vid]; return p && (p.busy || p.want !== qtyOf(G.cart, vid)) ? p.want : qtyOf(G.cart, vid); }
  function formsOf(vid) { return [].filter.call(document.querySelectorAll('form.gv2-card__add-form'), function (f) { return String(vidOf(f)) === String(vid); }); }
  function priceOf(form) { var b = form.querySelector('[data-gv2-price]'); return b ? parseInt(b.getAttribute('data-gv2-price'), 10) || 0 : 0; }
  function paintCards(cart) {
    document.querySelectorAll('form.gv2-card__add-form').forEach(function (f) { var v = vidOf(f); paintForm(f, pend[v] ? wantOf(v) : qtyOf(cart, v)); });
  }
  /* optimistic bar and badge from the known cart plus the pending changes */
  function guess() {
    var c = G.cart; if (!c) return null;
    var n = c.item_count, tot = c.total_price;
    for (var v in pend) { var d = wantOf(v) - qtyOf(c, v); if (d) { var f = formsOf(v)[0]; n += d; tot += d * (f ? priceOf(f) : 0); } }
    return { item_count: Math.max(0, n), total_price: Math.max(0, tot), items: c.items };
  }
  function paintGuess() { var g = guess(); if (!g) return; paintBar(g); G.counts(g.item_count); }
  function fail(vid, e) {
    if (pend[vid]) { pend[vid].busy = false; delete pend[vid]; }
    if (G.toast) G.toast('Couldn\'t update your cart', (e && e.message && !/^\d+$/.test(e.message)) ? e.message : 'Please try again', 'off');
    G.get(true).then(function (c) { paintCards(c); paintBar(c); }, function () {});
  }
  function pump(vid) {
    var p = pend[vid]; if (!p || p.busy) return;
    var have = qtyOf(G.cart, vid), target = p.want;
    if (target === have) { delete pend[vid]; G.set(G.cart); G.syncDrawer(); return; }
    p.busy = true;
    var req = have === 0
      ? fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(vid), quantity: target }] }) }).then(function (r) { if (!r.ok) return r.json().then(function (b) { throw new Error((b && (b.description || b.message)) || r.status); }); return G.get(true); })
      : fetch(root + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: String(vid), quantity: target }) }).then(function (r) { if (!r.ok) return r.json().then(function (b) { throw new Error((b && (b.description || b.message)) || r.status); }); return r.json(); });
    req.then(function (cart) {
      p.busy = false;
      G.cart = cart;
      var got = qtyOf(cart, vid);
      if (p.want === target && got < target && G.toast) { p.want = got; G.toast('Only ' + got + ' available right now', 'Your cart has the most we can send', 'off'); }
      if (p.want !== got) { pump(vid); paintGuess(); return; }
      delete pend[vid];
      bump = bump || target > have;
      if (!G.busy()) { G.set(cart); G.syncDrawer(); } else paintGuess();
    }).catch(function (e) { fail(vid, e); });
  }
  function setWant(form, want) {
    var vid = vidOf(form); if (!vid) return;
    var p = pend[vid] || (pend[vid] = { want: qtyOf(G.cart, vid), busy: false });
    if (!G.cart && !p.busy) { p.want = want; G.get().then(function () { paintGuess(); pump(vid); }, function (e) { fail(vid, e); }); }
    else p.want = want;
    formsOf(vid).forEach(function (f) { paintForm(f, want); });
    if (want > 0 && form.querySelector('.gv2-card__add') === document.activeElement) { var s = form.querySelector('[data-gv2-step-dir="1"]'); if (s) s.focus({ preventScroll: true }); } /* keyboard users stay on the control */
    bump = want > 0;
    paintGuess();
    if (G.cart) pump(vid);
  }
  /* capture phase, before the theme's product-form handler (which would open the drawer) */
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || !f.matches || !f.matches('form.gv2-card__add-form')) return;
    e.preventDefault(); e.stopImmediatePropagation();
    var vid = vidOf(f); setWant(f, Math.max(1, wantOf(vid) + 1));
  }, true);
  document.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('[data-gv2-step-dir]') : null;
    if (!b) return;
    var f = b.closest('form.gv2-card__add-form'); if (!f) return;
    e.preventDefault();
    setWant(f, Math.max(0, wantOf(vidOf(f)) + parseInt(b.getAttribute('data-gv2-step-dir'), 10)));
  });

  /* ---- floating cart bar ---- */
  var bar = null; var bump = false; var lastCount = null;
  function wanted() { return document.body && document.body.classList.contains('gv2-has-bar') && !document.querySelector('[data-gv2-cartpage]'); }
  function openCart() {
    var d = document.getElementById('cart-drawer') || document.querySelector('cart-drawer');
    if (d && typeof d.show === 'function') { d.show(); return; }
    window.location.href = root + 'cart';
  }
  function paintBar(cart) {
    if (!wanted()) return;
    var n = cart ? cart.item_count : 0;
    if (!bar) {
      if (!n) return;
      bar = document.createElement('div');
      bar.className = 'gv2-cbar'; bar.setAttribute('data-gv2-cbar', ''); bar.hidden = true;
      bar.innerHTML = '<button type="button" class="gv2-cbar__btn" data-gv2-cbar-open><span class="gv2-cbar__thumbs" aria-hidden="true"></span><span class="gv2-cbar__text"><strong class="gv2-cbar__sum"></strong><span class="gv2-cbar__sub"></span></span><span class="gv2-cbar__go">View cart<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg></span></button>';
      document.body.appendChild(bar);
      bar.addEventListener('click', function (e) { if (e.target.closest('[data-gv2-cbar-open]')) openCart(); });
    }
    if (!n) { bar.hidden = true; document.documentElement.removeAttribute('data-gv2-cbar'); lastCount = 0; return; }
    var d = (function () { var el = document.querySelector('[data-gv2-coupons]'); try { return el ? JSON.parse(el.textContent) : {}; } catch (e) { return {}; } })();
    var th = d.threshold || 0; var more = th - cart.total_price;
    bar.querySelector('.gv2-cbar__sum').textContent = n + (n === 1 ? ' item' : ' items') + ' · ' + G.money(cart.total_price);
    bar.querySelector('.gv2-cbar__sub').textContent = th ? (more > 0 ? 'Add ' + G.money(more) + ' for free shipping' : 'Free shipping unlocked') : 'Pay online or cash on delivery';
    bar.querySelector('.gv2-cbar__sub').setAttribute('data-free', th && more <= 0 ? 'yes' : 'no');
    var imgs = []; (cart.items || []).slice(0, 3).forEach(function (it) { if (it.image) imgs.push('<img src="' + esc(/^data:/.test(it.image) ? it.image : it.image + (String(it.image).indexOf('?') > -1 ? '&' : '?') + 'width=96') + '" alt="" width="32" height="32" loading="lazy">'); });
    bar.querySelector('.gv2-cbar__thumbs').innerHTML = imgs.join('');
    bar.querySelector('[data-gv2-cbar-open]').setAttribute('aria-label', 'View cart: ' + n + (n === 1 ? ' item' : ' items') + ', ' + G.money(cart.total_price));
    bar.hidden = false;
    document.documentElement.setAttribute('data-gv2-cbar', '');
    if (bump || (lastCount !== null && n > lastCount)) { bar.removeAttribute('data-bump'); void bar.offsetWidth; bar.setAttribute('data-bump', ''); }
    bump = false; lastCount = n;
  }
  document.addEventListener('gv2:cart', function (e) { paintCards(e.detail.cart); paintBar(e.detail.cart); });
  var boot = function () {
    if (document.querySelector('form.gv2-card__add-form') || wanted()) G.get().then(function (c) { paintCards(c); paintBar(c); }, function () {});
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', function () { if (G.cart) paintCards(G.cart); });
})();
